/**
 * Навигационные гварды.
 *
 * ⚠️ ГЛАВНОЕ, ЧТО НАДО ЗНАТЬ ПРО ЭТОТ ФАЙЛ: **гвард — это UX-аффорданс, а не
 * авторизация.** Авторитет — Go-бэкенд: `EventGuard` отдаёт 403 наблюдателю и
 * 404 не-участнику независимо от того, что решил роутер. Здесь мы всего лишь
 * экономим человеку заведомо отказной запрос и рисуем понятный экран вместо
 * мигания пустой таблицей. Ни одна проверка ниже не должна восприниматься как
 * контроль доступа.
 *
 * ───────────────────────────────────────────────────────────────────────────
 *  ДВЕ РАЗНЫЕ ПРОВЕРКИ РОЛЕЙ, И ОНИ РАБОТАЮТ ПО-РАЗНОМУ
 * ───────────────────────────────────────────────────────────────────────────
 *
 * **`meta.systemRoles`** — дешёвая: системная роль уже лежит в сторе после
 * `bootstrap()`, сеть не нужна.
 *
 * **`meta.eventManage`** — дорогая: роль **в мероприятии** не существует нигде,
 * кроме базы. Её нельзя ни положить в токен (составы меняются, пока токен жив),
 * ни вывести из системной. Чтобы её узнать, надо загрузить состав, то есть
 * сходить в сеть — и гвард это делает, дожидаясь `eventStore.load()`.
 *
 * ⚠️ И СПРАШИВАЕТ ОН `canManage`, А НЕ РОЛЬ. Ролей в мероприятии теперь три:
 * к владельцу и наблюдателю добавился «Наблюдатель+», которому можно писать
 * значения граф, подсвеченных для сканирования, — и НИЧЕГО больше. К экранам
 * управления (конструктор полей, справочник ролей, импорт, состав) он доступа
 * не получает, и получить его от появления новой роли не должен был: гвард
 * задаёт вопрос «можно ли здесь распоряжаться», а не «какая у тебя роль».
 * Сравнения ролей — ни `===`, ни `>=` — в этом файле нет и не должно
 * появиться: `canManage` перечисляет допустимые роли поимённо в одном месте
 * (`stores/event.ts`), а `role >= …` вдобавок ошиблось бы и по существу —
 * номер новой роли равен 3, то есть по порядку она стоит ПОСЛЕ наблюдателя.
 *
 * Почему это не откладывается в компонент: маршрут `…/participants/import`
 * наблюдателю не просто бесполезен — он показал бы форму загрузки файла,
 * которая гарантированно ответит 403 после того, как человек выберет файл и
 * дождётся разбора. Отказать до отрисовки честнее.
 *
 * Цена — одна лишняя навигационная задержка на первый вход в мероприятие;
 * дальше `load()` возвращает уже загруженное и не ходит в сеть.
 */
import type { Router } from 'vue-router'

import { loginDestination } from '@/lib/scanner/preferences'
import { useAuthStore } from '@/stores/auth'
import { useEventStore } from '@/stores/event'

/** Хвост `document.title`. */
const APP_TITLE = 'Аккредитация'

/**
 * Валидация `?redirect=` перед тем, как ей воспользоваться.
 *
 * Принимаем **только** строку, начинающуюся с одиночного `/`. Всё остальное —
 * `null`, то есть «редиректа не было».
 *
 * Что именно отсекается и почему:
 *   • `https://evil.com`  — абсолютный URL со схемой;
 *   • `//evil.com`        — protocol-relative: браузер читает это как хост,
 *                           а не как путь. Классический open-redirect в две строки;
 *   • `/\evil.com`        — браузеры нормализуют `\` в `/` в позиции authority,
 *                           так что это тот же `//evil.com` в маскировке;
 *   • не-строки           — `to.query.redirect` вполне может приехать массивом
 *                           (`?redirect=a&redirect=b`) или `null`.
 *
 * Экспортируется наружу: `LoginView` обязан гонять `route.query.redirect` через
 * эту же функцию, а не через свою копию проверки.
 */
export function safeRedirect(raw: unknown): string | null {
  if (typeof raw !== 'string') return null
  if (raw.length === 0 || raw[0] !== '/') return null
  // Второй символ `/` или `\` — попытка увести на чужой хост.
  if (raw[1] === '/' || raw[1] === '\\') return null
  return raw
}

/** `to.params.eventId` — строка либо массив (у повторяющегося параметра). */
function eventIdOf(params: Record<string, unknown>): string | null {
  const raw = params.eventId
  if (typeof raw === 'string' && raw.length > 0) return raw
  if (Array.isArray(raw) && typeof raw[0] === 'string' && raw[0].length > 0) return raw[0]
  return null
}

/**
 * Вешает `beforeEach`/`afterEach` на переданный роутер.
 *
 * Отдельной функцией, а не побочным эффектом импорта: так порядок «создали
 * роутер → повесили гварды» виден в `index.ts`, а не зависит от порядка импортов.
 */
export function registerGuards(router: Router): void {
  router.beforeEach(async (to) => {
    const auth = useAuthStore()

    /**
     * ★ КЛЮЧЕВАЯ СТРОКА ФАЙЛА.
     *
     * `bootstrap()` ждём **до любого решения о редиректе**. Access-токен живёт
     * только в памяти, поэтому на холодной загрузке (F5, открытие ссылки из
     * мессенджера, восстановление вкладок) `isAuthenticated === false` даже при
     * совершенно живой 30-дневной сессии. Редирект на `/login` в этот момент
     * обесценил бы «Запомнить меня» целиком.
     *
     * Ждать его на каждой навигации не дорого: в сторе он мемоизирован и после
     * первого вызова возвращает уже разрешённый промис.
     */
    try {
      await auth.bootstrap()
    } catch {
      /**
       * Восстановить сессию не удалось — сеть легла, сервер отдал 500, refresh
       * протух. Это **не** повод вешать навигацию: продолжаем как аноним, и
       * ниже человек уедет на `/login`. Молчаливое падение гварда оставило бы
       * белый экран без единого следа в UI, что гораздо хуже.
       */
    }

    // ── 1. Публичные маршруты: /login, 404 ──────────────────────────────
    if (to.meta.public) {
      if (auth.isAuthenticated && to.name === 'login') {
        // Уже вошли, а пришли на форму входа — уводим туда, куда собирались.
        const back = safeRedirect(to.query.redirect)
        // `back` на сам логин дал бы бесконечный цикл редиректов.
        if (back && router.resolve(back).name !== 'login') return back

        /**
         * Идти некуда — ведём туда, куда человек ходит обычно, а не в панель
         * по умолчанию. Оператор на входе выбрал сканер один раз и не должен
         * получать админку каждый раз, когда открыл вкладку с `/login`.
         *
         * Тот же выбор, что и на форме входа (`LoginView`), поэтому источник
         * один — `lib/scanner/preferences.ts`.
         */
        return loginDestination.get() === 'scanner'
          ? { name: 'scanner' }
          : { name: 'events' }
      }
      return true
    }

    // ── 2. Приватные маршруты: нужен вход ───────────────────────────────
    if (!auth.isAuthenticated) {
      // `fullPath`, а не `path`: у списков всё состояние (страница, поиск,
      // фильтры) лежит в query, и терять его при перелогине незачем.
      return { name: 'login', query: { redirect: to.fullPath } }
    }

    // ── 3. Системная роль ───────────────────────────────────────────────
    const allowed = to.meta.systemRoles
    // Пустой массив — это «никому», а не «всем»: см. `types/router.d.ts`.
    if (allowed && (auth.role === null || !allowed.includes(auth.role))) {
      return { name: 'forbidden' }
    }

    // ── 4. Роль внутри мероприятия ──────────────────────────────────────
    if (to.meta.eventManage) {
      const eventId = eventIdOf(to.params)
      // Маршрут с `eventManage` без `:eventId` — ошибка таблицы маршрутов,
      // а не пользователя. Пропускаем: сервер всё равно откажет, а гвард,
      // который молча запрещает из-за опечатки в `routes.ts`, отлаживается
      // мучительно.
      if (eventId) {
        const eventStore = useEventStore()
        // `load` никогда не бросает — ошибку кладёт в стор. Сюда мы приходим
        // либо со свежим скоупом, либо с тем же самым уже загруженным.
        await eventStore.load(eventId)

        /**
         * Мероприятия не видно вовсе (404 не-участнику либо опечатка в id) —
         * это не «недостаточно прав»: сервер намеренно не подтверждает
         * существование чужих мероприятий, и роутер не имеет права
         * подтверждать за него. Пускаем внутрь, layout покажет ошибку с
         * честным текстом «не найдено или вы не в составе».
         */
        if (eventStore.error !== null) return true

        // Мероприятие видно, но роли не хватает — вот здесь 403 честный:
        // человек уже знает, что мероприятие существует.
        //
        // ⚠️ `canManage`, и только он. `canEditScanFields` (право «Наблюдателя+»
        // на подсвеченные графы) сюда не подмешивается: маршруты под
        // `eventManage` — это конструктор полей, справочник ролей, импорт и
        // состав, где ему делать нечего. Сервер ответил бы 403 на каждое
        // действие, а экран показал бы формы, которые ничего не сохраняют.
        if (!eventStore.canManage) return { name: 'forbidden' }
      }
    }

    return true
  })

  /**
   * Заголовок вкладки. В `afterEach`, а не в `beforeEach`, — заголовок должен
   * отражать то, куда мы **пришли**, а не то, куда собирались: отменённая или
   * перенаправленная навигация иначе оставила бы чужой заголовок.
   */
  router.afterEach((to) => {
    const title = to.meta.title
    document.title = title ? `${title} — ${APP_TITLE}` : APP_TITLE
  })
}
